import Vue from 'vue';
import { mapActions, mapState } from 'vuex';
import _findIndex from 'lodash/findIndex';
import {
	asyncForEach,
	Avatar,
	RunnitNodeRun,
	RunnitNodeRunResult,
	RunnitUpload,
	buildFileNameForRunnitNodeRunResultDownload,
	buildFileNameForRunnitUploadDownload,
	buildFileNameForAvatarDownload,
	DbTimestamp,
	RUNNIT_NODE_RUN_RESULT_TYPE,
	RUNNIT_NODE_RUN_STATE,
} from '@run-diffusion/shared';
import {
	NODE_RUN_RESULT_SELECTION_MODE,
	RUNNIT_BULK_ACTIONS_TRIGGER_ID,
} from '@/views/Runnits/constants';
import { RunnitsImageSelectMixin } from './RunnitsImageSelectMixin';
import { db } from '@/firebase';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { TeamUserMixin } from './TeamUserMixin';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { SelectionState } from '@/store';
import { fetchRunnitImageSrc } from '@/utils/fetchRunnitImageSrc';

export const RunnitBulkActionsMixin = Vue.extend({
	mixins: [
		RunnitsImageSelectMixin,
		TeamUserMixin,
	],
	data () {
		return {
			RUNNIT_BULK_ACTIONS_TRIGGER_ID,

			bulkDeleteConfirmDialogOpen: false,
			deletingBulkImages: false,
			downloadingBulkImages: false,
		};
	},
	computed: {
		...mapState([
			'runnitState',
			'user',
		]),
		isRunnitBulkActionMenuOpen () {
			return this.currentSelectionState && this.currentSelectionState.triggeringId === this.RUNNIT_BULK_ACTIONS_TRIGGER_ID;
		},
		selectedImagesCount () {
			return this._get(this.getSelectionStateByTrigger(this.RUNNIT_BULK_ACTIONS_TRIGGER_ID), 'selectedNodeRunResults.length', 0) +
				this._get(this.getSelectionStateByTrigger(this.RUNNIT_BULK_ACTIONS_TRIGGER_ID), 'selectedUploads.length', 0) +
				this._get(this.getSelectionStateByTrigger(this.RUNNIT_BULK_ACTIONS_TRIGGER_ID), 'selectedAvatars.length', 0);
		},
		selectedImagesContainModeratedResult () {
			const selectionState: SelectionState = this.getSelectionStateByTrigger(this.RUNNIT_BULK_ACTIONS_TRIGGER_ID);
			const selectedNodeRunResults: RunnitNodeRunResult[] = this._get(selectionState, 'selectedNodeRunResults', []);

			if (selectedNodeRunResults.some((result: RunnitNodeRunResult) => !!result?.isNsfw)) {
				return true;
			}

			return Object.values(this._get(selectionState, 'selectedNodeRunsMap', {})).some(({ nodeRun, results }: {
				nodeRun: RunnitNodeRun
				results: RunnitNodeRunResult[]
			}) => {
				return nodeRun?.state === RUNNIT_NODE_RUN_STATE.NSFW ||
					(results || []).some((result: RunnitNodeRunResult) => !!result?.isNsfw);
			});
		},
		downloadSelectedImagesDisabled () {
			return !this.selectedImagesCount ||
				this.deletingBulkImages ||
				this.downloadingBulkImages ||
				this.selectedImagesContainModeratedResult;
		},
		downloadSelectedImagesTooltipMessage () {
			return this.selectedImagesContainModeratedResult
				? 'Cannot download moderated results'
				: 'Download';
		},
	},
	methods: {
		...mapActions([
			'updateRunnitState',
			'updateSnackbar',
		]),
		setBulkActionMenuOpen (isOpen) {
			if (isOpen) {
				this.enterSelectionMode(NODE_RUN_RESULT_SELECTION_MODE.MULTIPLE, this.RUNNIT_BULK_ACTIONS_TRIGGER_ID, null, ['IMG', 'VID']);
				return;
			}
			this.exitSelectionMode(this.RUNNIT_BULK_ACTIONS_TRIGGER_ID);
			this.clearSelectionsFromState(this.RUNNIT_BULK_ACTIONS_TRIGGER_ID);
		},
		setDeleteImagesDialog (isOpen) {
			// Note: make sure to add a watcher on `bulkDeleteConfirmDialogOpen` to use the double confirmation dialog ref to call the reset()
			this.bulkDeleteConfirmDialogOpen = isOpen;
		},
		async handleDownloadSelectedImages (zipFolderName = 'images.zip') {
			if (this.selectedImagesContainModeratedResult) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Cannot download moderated results',
					show: true,
				});
				return;
			}

			if (this.selectedImagesCount > 1) {
				this.handleDownloadSelectedImagesZip(zipFolderName);
			} else {
				this.handleDownloadSelectedImagesIndividually();
			}
		},
		async handleDownloadSelectedImagesZip (zipFolderName = 'images.zip') {
			try {
				this.downloadingBulkImages = true;
				const zip: JSZip = new JSZip();
				await asyncForEach(this.getSelectionStateByTrigger(this.RUNNIT_BULK_ACTIONS_TRIGGER_ID).selectedNodeRunResults, async (nodeRunResult: RunnitNodeRunResult) => {
					const { imageUrl, fileName } = await this.fetchSrc(nodeRunResult, 'nodeRunResult');
					const fileData = await this.convertUrlToBase64(imageUrl);
					const builtFileName: string = buildFileNameForRunnitNodeRunResultDownload(
						fileName,
						nodeRunResult,
						this.getSelectionStateByTrigger(this.RUNNIT_BULK_ACTIONS_TRIGGER_ID).selectedNodeRunsMap[nodeRunResult.nodeRunId]?.nodeRun || null,
					);
					zip.file(builtFileName, fileData.split(',')[1], { base64: true });
				});
				await asyncForEach(this.getSelectionStateByTrigger(this.RUNNIT_BULK_ACTIONS_TRIGGER_ID).selectedUploads, async (upload: RunnitUpload) => {
					const { imageUrl, fileName } = await this.fetchSrc(upload, 'upload');
					const fileData = await this.convertUrlToBase64(imageUrl);
					const builtFileName: string = buildFileNameForRunnitUploadDownload(
						fileName,
						upload,
					);
					zip.file(builtFileName, fileData.split(',')[1], { base64: true });
				});
				await asyncForEach(this.getSelectionStateByTrigger(this.RUNNIT_BULK_ACTIONS_TRIGGER_ID).selectedAvatars, async (avatar: Avatar) => {
					const { imageUrl, fileName } = await this.fetchSrc(avatar, 'avatar');
					const fileData = await this.convertUrlToBase64(imageUrl);
					const builtFileName: string = buildFileNameForAvatarDownload(
						fileName,
						avatar,
					);
					zip.file(builtFileName, fileData.split(',')[1], { base64: true });
				});

				zip.generateAsync({ type: 'blob' }).then((content: Blob) => {
					saveAs(content, zipFolderName);
				});

				this.clearSelectionsFromState(this.RUNNIT_BULK_ACTIONS_TRIGGER_ID);
				this.exitSelectionMode(this.RUNNIT_BULK_ACTIONS_TRIGGER_ID);
			} catch (e) {
				console.error(e);
			} finally {
				this.downloadingBulkImages = false;
			}
		},
		async handleDownloadSelectedImagesIndividually () {
			try {
				this.downloadingBulkImages = true;
				const wait: Function = (): Promise<void> => new Promise(resolve => setTimeout(resolve, 250));

				await asyncForEach(this.getSelectionStateByTrigger(this.RUNNIT_BULK_ACTIONS_TRIGGER_ID).selectedNodeRunResults, async (nodeRunResult: RunnitNodeRunResult) => {
					const { imageUrl, fileName } = await this.fetchSrc(nodeRunResult, 'nodeRunResult');
					const builtFileName: string = buildFileNameForRunnitNodeRunResultDownload(
						fileName,
						nodeRunResult,
						this.getSelectionStateByTrigger(this.RUNNIT_BULK_ACTIONS_TRIGGER_ID).selectedNodeRunsMap[nodeRunResult.nodeRunId]?.nodeRun || null,
					);
					await this.downloadImageByUrl(imageUrl, builtFileName);
					await wait();
				});
				await asyncForEach(this.getSelectionStateByTrigger(this.RUNNIT_BULK_ACTIONS_TRIGGER_ID).selectedUploads, async (upload: RunnitUpload) => {
					const { imageUrl, fileName } = await this.fetchSrc(upload, 'upload');
					await this.downloadImageByUrl(imageUrl, fileName);
					await wait();
				});

				await asyncForEach(this.getSelectionStateByTrigger(this.RUNNIT_BULK_ACTIONS_TRIGGER_ID).selectedAvatars, async (avatar: Avatar) => {
					const { imageUrl, fileName } = await this.fetchSrc(avatar, 'avatar');
					await this.downloadImageByUrl(imageUrl, fileName);
					await wait();
				});

				this.clearSelectionsFromState(this.RUNNIT_BULK_ACTIONS_TRIGGER_ID);
				this.exitSelectionMode(this.RUNNIT_BULK_ACTIONS_TRIGGER_ID);
			} catch (e) {
				console.error(e);
			} finally {
				this.downloadingBulkImages = false;
			}
		},
		async fetchSrc (input: RunnitNodeRunResult | RunnitUpload | Avatar, useCase: 'nodeRunResult' | 'upload' | 'avatar') {
			return fetchRunnitImageSrc(input, useCase);
		},
		async convertUrlToBase64 (imageUrl) {
			try {
				const response = await fetch(imageUrl);
				const blob = await response.blob();

				return new Promise((resolve, reject) => {
					const reader: FileReader = new FileReader();
					reader.onloadend = () => resolve(reader.result);
					reader.onerror = reject;
					reader.readAsDataURL(blob);
				})
			} catch (err) {
				console.error('Error converting image to base64 for download', err);
			}
		},
		async downloadImageByUrl (imageUrl: string, fileName: string) {
			try {
				// For PSD files, use a more robust download approach
				if (fileName.toLowerCase().endsWith('.psd')) {
					// Use fetch with proper headers for binary files
					const response = await fetch(imageUrl, {
						method: 'GET',
						headers: {
							'Accept': 'application/octet-stream, */*',
						},
					});
					
					if (!response.ok) {
						throw new Error(`HTTP error! status: ${response.status}`);
					}
					
					const blob: Blob = await response.blob();
					
					// Validate that we got a reasonable file size
					if (blob.size < 1000) {
						throw new Error(`PSD file too small: ${blob.size} bytes`);
					}
					
					const url: string = URL.createObjectURL(blob);
					const link: HTMLAnchorElement = document.createElement('a');
					link.href = url;
					link.download = fileName;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
					URL.revokeObjectURL(url);
				} else {
					// Use the original method for non-PSD files
					const blob: Blob = await (await fetch(imageUrl)).blob();
					const url: string = URL.createObjectURL(blob);

					const link: HTMLAnchorElement = document.createElement('a');
					link.href = url;
					link.download = fileName;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
					URL.revokeObjectURL(url);
				}
			} catch (err) {
				console.error(`failed to download file: ${fileName}`, err);
				throw err; // Re-throw to allow error handling in calling code
			}
		},
		// Hosts with broader cross-Team context can override this method. The
		// default preserves the existing active-Team permission behavior.
		canDeleteNodeRun (nodeRun: RunnitNodeRun): boolean {
			return this.hasManageAllGenerationsPermission || nodeRun.userId === this.user.id;
		},
		async deleteNodeRunResultBatch (batch: RunnitNodeRunResult[], nodeRunId: string) {
			const count: number = batch.length;
			const nodeRunRef = db.doc(`runnitNodeRuns/${nodeRunId}`);
			const nodeRun: RunnitNodeRun = (await nodeRunRef.get()).data() as RunnitNodeRun;

			if (!this.canDeleteNodeRun(nodeRun)) {
				return { success: false, count, reason: 'Not your generations' };
			}

			const deletedAt: DbTimestamp = new Date() as DbTimestamp;
			const deletedByUserId: string = this.user.id;
			const results: RunnitNodeRunResult[] = (nodeRun.results || []).map((nodeRunResult: RunnitNodeRunResult) => {
				let result: RunnitNodeRunResult = { ...nodeRunResult };
				if (_findIndex(batch, (nrr: RunnitNodeRunResult) => (nrr.uuid === nodeRunResult.uuid)) !== -1) {
					result = {
						...result,
						deletedAt,
						deletedByUserId,
					}
				}
				return result;
			});
			if (results.every((nodeRunResult: RunnitNodeRunResult) => !!nodeRunResult.deletedAt)) {
				// every result is deleted also delete the nodeRun
				try {
					const updatedNodeRun: Partial<RunnitNodeRun> = {
						deletedAt,
						deletedByUserId: this.user.id,
					};
					if (results.length) {
						updatedNodeRun.results = results;
					}
					await nodeRunRef.update(updatedNodeRun);
				} catch (err) {
					console.error('Error deleting node run', err);
					return { success: false, count, reason: 'Internal Error' }
				}
			} else {
				// there are still some results that haven't been deleted
				try {
					await nodeRunRef.update({
						...(results.length ? { results } : {}),
					});
				} catch (err) {
					console.error('Error deleting node run results', err);
					return { success: false, count, reason: 'Internal Error' }
				}
			}

			return { success: true, count, reason: 'We did good' };
		},
		async deleteUpload (upload: RunnitUpload) {
			const uploadRef = db.doc(`runnitUploads/${upload.id}`);

			if (upload.userId !== this.user.id) {
				return { success: false, count: 1, reason: 'Did not upload' };
			}

			try {
				await uploadRef.update({
					deletedAt: new Date(),
					deletedByUserId: this.user.id,
				});
			} catch (err) {
				console.error('Error deleting upload', err);
				return { success: false, count: 1, reason: 'Internal Error' };
			}

			return { success: true, count: 1, reason: 'We did good' };
		},
		async deleteAvatar (avatar: Avatar) {
			const avatarRef = db.doc(`avatars/${avatar.id}`);

			if (avatar.userId !== this.user.id) {
				return { success: false, count: 1, reason: 'Did not upload' };
			}

			try {
				await avatarRef.update({
					deletedAt: new Date(),
					deletedByUserId: this.user.id,
				});
			} catch (err) {
				console.error('Error deleting avatar', err);
				return { success: false, count: 1, reason: 'Internal Error' };
			}

			return { success: true, count: 1, reason: 'We did good' };
		},
		async handleDeleteSelectedImages () {
			this.deletingBulkImages = true;
			try {
				const resultsMap: Record<string, number> = {};
				const selectionState: SelectionState = this.getSelectionStateByTrigger(this.RUNNIT_BULK_ACTIONS_TRIGGER_ID);

				await asyncForEach(Object.keys(selectionState.selectedNodeRunsMap), async (nodeRunId: string) => {
					const nodeRunResults: RunnitNodeRunResult[] = selectionState.selectedNodeRunsMap[nodeRunId].results;
					const { success, count, reason } = await this.deleteNodeRunResultBatch(nodeRunResults, nodeRunId);
					if (!success) {
						if (!resultsMap[reason]) {
							resultsMap[reason] = 0;
						}
						resultsMap[reason] = resultsMap[reason] + count;
					} else {
						this.updateRunnitState({
							deletedImages: {
								...this.runnitState.deletedImages,
								nodeRunResults: [...this.runnitState.deletedImages.nodeRunResults, ...nodeRunResults],
							}
						});
					}
				});

				await asyncForEach(selectionState.selectedUploads, async (upload: RunnitUpload) => {
					const { success, count, reason } = await this.deleteUpload(upload);
					if (!success) {
						if (!resultsMap[reason]) {
							resultsMap[reason] = 0;
						}
						resultsMap[reason] = resultsMap[reason] + count;
					} else {
						this.updateRunnitState({
							deletedImages: {
								...this.runnitState.deletedImages,
								uploads: [...this.runnitState.deletedImages.uploads, upload],
							}
						});
					}
				});

				await asyncForEach(selectionState.selectedAvatars, async (avatar: Avatar) => {
					const { success, count, reason } = await this.deleteAvatar(avatar);
					if (!success) {
						if (!resultsMap[reason]) {
							resultsMap[reason] = 0;
						}
						resultsMap[reason] = resultsMap[reason] + count;
					} else {
						this.updateRunnitState({
							deletedImages: {
								...this.runnitState.deletedImages,
								avatars: [...this.runnitState.deletedImages.avatars, avatar],
							}
						});
					}
				});

				if (Object.keys(resultsMap).length) {
					const message = Object.keys(resultsMap).reduce((message, reason, index) => {
						return message + ` ${index ? '| ' : ''}${resultsMap[reason]}--${reason}`;
					}, 'Issues deleting: ');
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message,
						show: true,
						timeout: 30000,
					});
				}

				this.setBulkActionMenuOpen(false);
			} catch (err) {
				console.error('Error deleting images', err);
			} finally {
				this.deletingBulkImages = false;
				this.setDeleteImagesDialog(false);
			}
		},
	},
});
